(() => {
  if (window.guardenWarrantyReady) return;
  window.guardenWarrantyReady = true;
  const states = new WeakMap();
  const allowed = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4', 'A', 'BLOCKQUOTE']);

  function copyText(source, base) {
    const fragment = document.createDocumentFragment();
    for (const node of source.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) { fragment.append(document.createTextNode(node.textContent)); continue; }
      if (node.nodeType !== Node.ELEMENT_NODE || ['SCRIPT', 'STYLE', 'IFRAME', 'FORM', 'BUTTON', 'SVG'].includes(node.tagName)) continue;
      if (!allowed.has(node.tagName)) { fragment.append(copyText(node, base)); continue; }
      const element = document.createElement(node.tagName === 'H2' ? 'h3' : node.tagName.toLowerCase());
      if (node.tagName === 'A') {
        const url = new URL(node.getAttribute('href') || '', base);
        if (['https:', 'http:', 'mailto:', 'tel:'].includes(url.protocol)) {
          element.href = url.href;
          if (url.origin !== location.origin && ['https:', 'http:'].includes(url.protocol)) { element.target = '_blank'; element.rel = 'noopener'; }
        }
      }
      element.append(copyText(node, base));
      fragment.append(element);
    }
    return fragment;
  }

  function disclosure(title, content, base) {
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    summary.textContent = title;
    const body = document.createElement('div');
    body.className = 'gd-warranty__text';
    body.append(copyText(content, base));
    details.append(summary, body);
    return details;
  }

  function warrantyContent(doc, base) {
    const sections = Array.from(doc.querySelectorAll('main .text-section'));
    const content = document.createElement('div');
    const summarySection = sections.find(section => section.querySelector('.text-section__content > ul') && !section.querySelector('.text-section__content h2'));
    if (!summarySection || sections.length < 3) throw new Error('Warranty content unavailable');
    const summary = document.createElement('section');
    summary.className = 'gd-warranty__summary';
    const heading = document.createElement('h3');
    heading.textContent = summarySection.querySelector('.text-section__heading')?.textContent.trim() || '';
    summary.append(heading, copyText(summarySection.querySelector('.text-section__content'), base));
    content.append(summary);
    const groups = [];
    for (const section of sections) {
      if (section === summarySection) continue;
      const text = section.querySelector('.text-section__content');
      if (!text) continue;
      let title = section.querySelector('.text-section__heading')?.textContent.trim() || '';
      let body = document.createElement('div');
      for (const node of text.childNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H2') {
          if (body.textContent.trim()) groups.push({ title, body });
          title = node.textContent.trim();
          body = document.createElement('div');
        } else body.append(node.cloneNode(true));
      }
      if (body.textContent.trim()) groups.push({ title, body });
    }
    if (groups.length < 5) throw new Error('Warranty terms unavailable');
    const priority = ['Was abgedeckt ist.', 'Was wir leisten', 'So machst du die Garantie geltend.', 'Was nicht abgedeckt ist.', 'Für welche Produkte sie gilt.', 'Wie lange sie gilt.', 'Wo sie gilt.', 'Übertragbarkeit.', 'Was diese Garantie ist - und was nicht.', 'Wer die Garantie gibt.'];
    groups.sort((a, b) => {
      const ai = priority.indexOf(a.title), bi = priority.indexOf(b.title);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
    for (const group of groups) content.append(disclosure(group.title, group.body, base));
    const faq = doc.querySelector('main .faq-simple');
    if (faq) {
      const details = document.createElement('details');
      const label = document.createElement('summary');
      label.textContent = faq.querySelector('.faq-simple__heading')?.textContent.trim() || '';
      const body = document.createElement('div');
      body.className = 'gd-warranty__text';
      for (const item of faq.querySelectorAll('.faq-simple__item')) {
        const title = item.querySelector('.faq-simple__item-title-text');
        const answer = item.querySelector('.faq-simple__item-inner');
        if (title && answer) body.append(disclosure(title.textContent.trim(), answer, base));
      }
      if (body.childElementCount) { details.append(label, body); content.append(details); }
    }
    return content;
  }

  function referenceContent(doc, base) {
    const main = doc.querySelector('main');
    if (!main) throw new Error('Page content unavailable');
    const content = document.createElement('div');
    content.className = 'gd-warranty__text';
    const regions = main.querySelectorAll('.text-section__content, .rte');
    for (const region of regions) {
      if (region.closest('.apps-section') || Array.from(regions).some(other => other !== region && other.contains(region))) continue;
      const title = region.closest('.text-section')?.querySelector('.text-section__heading');
      if (title) { const heading = document.createElement('h3'); heading.textContent = title.textContent; content.append(heading); }
      content.append(copyText(region, base));
    }
    if (!content.textContent.trim()) throw new Error('Page content unavailable');
    return { content, title: main.querySelector('h1, h2')?.textContent.trim() || doc.title };
  }

  async function load(dialog, path, reference = false) {
    const state = states.get(dialog);
    if (!state) return;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    state.currentPath = path;
    state.reference = reference;
    const target = dialog.querySelector('[data-warranty-content]');
    const status = dialog.querySelector('[data-warranty-status]');
    const error = dialog.querySelector('[data-warranty-error]');
    target.replaceChildren();
    target.setAttribute('aria-busy', 'true');
    status.hidden = false;
    error.hidden = true;
    dialog.querySelector('[data-warranty-back]').hidden = !reference;
    dialog.querySelector('[data-warranty-fallback]').href = path;
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('Unsupported source');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Page unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = reference ? referenceContent(doc, url) : { content: warrantyContent(doc, url), title: state.title };
      if (state.controller !== controller || !dialog.open) return;
      target.append(result.content);
      dialog.querySelector('h2').textContent = result.title;
      if (!reference) state.cached = result.content.cloneNode(true);
      dialog.querySelector('.gd-warranty__body').scrollTop = 0;
    } catch (e) {
      if (state.controller !== controller || !dialog.open) return;
      error.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (state.controller === controller) { status.hidden = true; target.removeAttribute('aria-busy'); }
    }
  }

  function back(dialog) {
    const state = states.get(dialog);
    if (!state) return;
    state.controller?.abort();
    state.controller = null;
    state.reference = false;
    dialog.querySelector('h2').textContent = state.title;
    dialog.querySelector('[data-warranty-back]').hidden = true;
    dialog.querySelector('[data-warranty-error]').hidden = true;
    dialog.querySelector('[data-warranty-status]').hidden = true;
    const content = dialog.querySelector('[data-warranty-content]');
    content.removeAttribute('aria-busy');
    if (state.cached) {
      content.replaceChildren(state.cached.cloneNode(true));
      dialog.querySelector('.gd-warranty__body').scrollTop = state.scroll || 0;
    } else load(dialog, dialog.dataset.source);
    dialog.querySelector('[data-warranty-close]').focus();
  }

  function open(trigger) {
    const dialog = document.querySelector('[data-guarden-warranty]');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;
    const state = states.get(dialog) || { title: dialog.querySelector('h2').textContent };
    state.trigger = trigger;
    state.overflow = document.body.style.overflow;
    states.set(dialog, state);
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    if (state.reference) back(dialog);
    else if (!state.cached) load(dialog, dialog.dataset.source);
    dialog.querySelector('[data-warranty-close]').focus();
    return true;
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-guarden-warranty-open]');
    if (trigger) {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (open(trigger)) event.preventDefault();
      return;
    }
    const dialog = event.target.closest('[data-guarden-warranty]');
    if (!dialog) return;
    if (event.target.closest('[data-warranty-close]')) { dialog.close(); return; }
    if (event.target.closest('[data-warranty-back]')) { back(dialog); return; }
    if (event.target.closest('[data-warranty-retry]')) { const state = states.get(dialog); load(dialog, state.currentPath, state.reference); return; }
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
    const link = event.target.closest('[data-warranty-content] a[href]');
    if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      const url = new URL(link.href);
      if (url.origin === location.origin && /\/pages\//.test(url.pathname)) {
        event.preventDefault();
        const state = states.get(dialog);
        if (!state.reference) { state.cached = dialog.querySelector('[data-warranty-content]').firstElementChild.cloneNode(true); state.scroll = dialog.querySelector('.gd-warranty__body').scrollTop; }
        load(dialog, url.href, true);
        dialog.querySelector('[data-warranty-back]').focus();
      }
    }
  });
  document.addEventListener('close', event => {
    const dialog = event.target;
    if (!(dialog instanceof Element) || !dialog.matches('[data-guarden-warranty]')) return;
    const state = states.get(dialog);
    if (!state) return;
    state.controller?.abort();
    document.body.style.overflow = state.overflow;
    if (state.trigger?.isConnected) state.trigger.focus({ preventScroll: true });
  }, true);
  document.addEventListener('shopify:section:select', event => {
    if (event.target.querySelector('[data-guarden-warranty]')) open(null);
  });
})();
